import React, { useContext, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  DownloadOptionData,
  MovieActorData,
  MovieFullData,
  MovieTaxonomyData,
  MovieUpdateRequest,
  SubtitleData,
} from "../../entry/server/movie.ts";
import { videoRepo } from "../../repo/video-repo.ts";
import { AppContext } from "../../context/app-context.tsx";
import AdminPageTitle from "../admin-pages/admin-page-title.tsx";
import HtmlEditor from "../../componrnts/html-editor.tsx";
import { ASSETS_BASE_URL } from "../../utils/constants.ts";

const inputClass =
  "w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100 outline-none transition focus:border-indigo-400";

function assetUrl(path: string) {
  if (!path) return "";
  return path.includes("http") ? path : `${ASSETS_BASE_URL}${path}`;
}

function normalizeMovie(data: MovieFullData): MovieFullData {
  return {
    ...data,
    full_description: data.full_description ?? "",
    is_featured: Boolean(data.is_featured),
    use_auto_description: Boolean(data.use_auto_description),
    download_options: data.download_options ?? [],
    subtitles: data.subtitles ?? [],
    genres: data.genres ?? [],
    countries: data.countries ?? [],
    languages: data.languages ?? [],
    networks: data.networks ?? [],
    directors: data.directors ?? [],
    categories: data.categories ?? [],
    actors: data.actors ?? [],
    movie_stats: data.movie_stats ?? {
      likes: 0,
      dislikes: 0,
      views: 0,
      created_at: "",
      updated_at: "",
    },
  };
}

function emptyDownloadOption(): DownloadOptionData {
  return {
    id: 0,
    season: 0,
    episode: 0,
    quality: "",
    quality_code: "",
    format: "",
    type: "",
    encoder: "",
    size: 0,
    url: "",
    is_x265: false,
    is_10bit: false,
    can_play_online: false,
  };
}

function emptySubtitle(): SubtitleData {
  return {
    id: 0,
    name: "",
    season: 0,
    episode: 0,
    format: "",
    size: 0,
    dubbed: false,
    url: "",
  };
}

type TaxonomyKey = "genres" | "countries" | "languages" | "networks" | "directors" | "categories";

const TAXONOMY_SEARCH_TYPES: Record<TaxonomyKey, Parameters<typeof videoRepo.searchTaxonomies>[1]> = {
  genres: "genre",
  countries: "country",
  languages: "language",
  networks: "network",
  directors: "director",
  categories: "category",
};

const emptyTaxonomyQueries = (): Record<TaxonomyKey, string> => ({
  genres: "",
  countries: "",
  languages: "",
  networks: "",
  directors: "",
  categories: "",
});

const emptyTaxonomyResults = (): Record<TaxonomyKey, MovieTaxonomyData[]> => ({
  genres: [],
  countries: [],
  languages: [],
  networks: [],
  directors: [],
  categories: [],
});

const moviTypeOptions = [
  { label: "فیلم", value: "movie" },
  { label: "سریال", value: "series" },
  { label: "انیمیشن", value: "animation" },
  { label: "انیمه", value: "anime" },
];

const downloadOptionTypeOptions = [
  { label: "دوبله فارسی", value: "farsi_dubbed" },
  { label: "زیرنویس فارسی چسبیده", value: "with_subtitle" },
  { label: "بدون زیرنویس", value: "without_subtitle" },
];

export default function MovieDetail() {
  const { id } = useParams();
  const movieId = Number(id);
  const navigate = useNavigate();
  const { setMessage } = useContext(AppContext);
  const [draft, setDraft] = useState<MovieFullData | null>(null);
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [taxQuery, setTaxQuery] = useState<Record<TaxonomyKey, string>>(() => emptyTaxonomyQueries());
  const [taxResults, setTaxResults] = useState<Record<TaxonomyKey, MovieTaxonomyData[]>>(() => emptyTaxonomyResults());
  const [taxSearching, setTaxSearching] = useState<Partial<Record<TaxonomyKey, boolean>>>({});
  const [actorQuery, setActorQuery] = useState("");
  const [actorResults, setActorResults] = useState<MovieTaxonomyData[]>([]);
  const [actorSearching, setActorSearching] = useState(false);

  useEffect(() => {
    if (!movieId) {
      setMessage({ type: "error", message: "شناسه فیلم معتبر نیست." });
      navigate("/admin/movies");
      return;
    }

    const fetchMovie = async () => {
      setLoading(true);
      try {
        const response = await videoRepo.getMovie(movieId);
        if (!response.status || !response.data) {
          setMessage({ type: "error", message: response.message || "دریافت جزئیات فیلم ناموفق بود." });
          navigate("/admin/movies");
          return;
        }
        setDraft(normalizeMovie(response.data));
        setTaxQuery(emptyTaxonomyQueries());
        setTaxResults(emptyTaxonomyResults());
        setTaxSearching({});
        setActorQuery("");
        setActorResults([]);
        setActorSearching(false);
      } catch {
        setMessage({ type: "error", message: "خطا در ارتباط با سرور. دوباره تلاش کنید." });
        navigate("/admin/movies");
      } finally {
        setLoading(false);
      }
    };

    fetchMovie();
  }, [movieId, navigate, setMessage]);

  const onSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!draft) return;

    setIsSubmitting(true);
    try {
      const { id: _id, genres, countries, languages, networks, directors, categories, actors, ...rest } = draft;
      void _id;
      const payload: MovieUpdateRequest = {
        ...rest,
        genre_ids: (genres ?? []).map((t) => t.id).filter((id) => id > 0),
        country_ids: (countries ?? []).map((t) => t.id).filter((id) => id > 0),
        language_ids: (languages ?? []).map((t) => t.id).filter((id) => id > 0),
        network_ids: (networks ?? []).map((t) => t.id).filter((id) => id > 0),
        director_ids: (directors ?? []).map((t) => t.id).filter((id) => id > 0),
        actor_ids: (actors ?? []).map((t) => t.id).filter((id) => id > 0),
        category_ids: (categories ?? []).map((t) => t.id).filter((id) => id > 0),
      };
      const response = await videoRepo.updateMovie(movieId, payload);
      if (!response.status) {
        setMessage({ type: "error", message: response.message || "ذخیره تغییرات ناموفق بود." });
        return;
      }
      setMessage({ type: "success", message: "تغییرات با موفقیت ذخیره شد." });
    } catch {
      setMessage({ type: "error", message: "خطا در ارتباط با سرور. دوباره تلاش کنید." });
    } finally {
      setIsSubmitting(false);
    }
  };

  const onDelete = async () => {
    if (!draft) return;
    setIsDeleting(true);
    try {
      const response = await videoRepo.deleteMovie(draft.id);
      if (!response.status) {
        setMessage({ type: "error", message: response.message || "حذف فیلم ناموفق بود." });
        return;
      }
      setMessage({ type: "success", message: "فیلم با موفقیت حذف شد." });
      navigate("/admin/movies");
    } catch {
      setMessage({ type: "error", message: "خطا در ارتباط با سرور. دوباره تلاش کنید." });
    } finally {
      setIsDeleting(false);
    }
  };

  const removeTaxonomyRow = (key: TaxonomyKey, index: number) => {
    setDraft((d) => {
      if (!d) return d;
      const list = d[key] ?? [];
      return { ...d, [key]: list.filter((_, i) => i !== index) };
    });
  };

  const addTaxonomyFromSearch = (key: TaxonomyKey, item: MovieTaxonomyData) => {
    setDraft((d) => {
      if (!d) return d;
      const list = d[key] ?? [];
      if (!item.id || list.some((t) => t.id === item.id)) return d;
      return { ...d, [key]: [...list, { id: item.id, name: item.name, slug: item.slug }] };
    });
  };

  const searchTaxonomyKind = async (key: TaxonomyKey) => {
    const query = (taxQuery[key] ?? "").trim();
    if (!query) {
      setTaxResults((r) => ({ ...emptyTaxonomyResults(), ...r, [key]: [] }));
      return;
    }
    setTaxSearching((s) => ({ ...s, [key]: true }));
    try {
      const response = await videoRepo.searchTaxonomies(query, TAXONOMY_SEARCH_TYPES[key]);
      if (!response.status || !response.data) {
        setMessage({ type: "error", message: response.message || "جستجوی تاکسونومی ناموفق بود." });
        setTaxResults((r) => ({ ...emptyTaxonomyResults(), ...r, [key]: [] }));
        return;
      }
      setTaxResults((r) => ({ ...emptyTaxonomyResults(), ...r, [key]: response.data ?? [] }));
    } catch {
      setMessage({ type: "error", message: "خطا در جستجو. دوباره تلاش کنید." });
      setTaxResults((r) => ({ ...emptyTaxonomyResults(), ...r, [key]: [] }));
    } finally {
      setTaxSearching((s) => ({ ...s, [key]: false }));
    }
  };

  const searchActors = async () => {
    const query = actorQuery.trim();
    if (!query) {
      setActorResults([]);
      return;
    }
    setActorSearching(true);
    try {
      const response = await videoRepo.searchTaxonomies(query, "actor");
      if (!response.status || !response.data) {
        setMessage({ type: "error", message: response.message || "جستجوی بازیگر ناموفق بود." });
        setActorResults([]);
        return;
      }
      setActorResults(response.data ?? []);
    } catch {
      setMessage({ type: "error", message: "خطا در جستجو. دوباره تلاش کنید." });
      setActorResults([]);
    } finally {
      setActorSearching(false);
    }
  };

  const addActorFromSearch = (item: MovieTaxonomyData) => {
    setDraft((d) => {
      if (!d) return d;
      if (!item.id || d.actors.some((a) => a.id === item.id)) return d;
      const next: MovieActorData = {
        id: item.id,
        name: item.name,
        role: "",
        is_star: false,
      };
      return { ...d, actors: [...d.actors, next] };
    });
  };

  const removeActorRow = (index: number) => {
    setDraft((d) => (d ? { ...d, actors: d.actors.filter((_, i) => i !== index) } : d));
  };

  const updateActorRow = (index: number, field: "role" | "is_star", value: string | boolean) => {
    setDraft((d) => {
      if (!d) return d;
      const list = [...d.actors];
      list[index] = { ...list[index], [field]: value };
      return { ...d, actors: list };
    });
  };

  const updateDownloadRow = (index: number, field: keyof DownloadOptionData, value: string | number | boolean) => {
    setDraft((d) => {
      if (!d) return d;
      const list = [...d.download_options];
      list[index] = { ...list[index], [field]: value };
      return { ...d, download_options: list };
    });
  };

  const addDownloadRow = () => {
    setDraft((d) => (d ? { ...d, download_options: [...d.download_options, emptyDownloadOption()] } : d));
  };

  const removeDownloadRow = (index: number) => {
    setDraft((d) => (d ? { ...d, download_options: d.download_options.filter((_, i) => i !== index) } : d));
  };

  const updateSubtitleRow = (index: number, field: keyof SubtitleData, value: string | number | boolean) => {
    setDraft((d) => {
      if (!d) return d;
      const list = [...d.subtitles];
      list[index] = { ...list[index], [field]: value };
      return { ...d, subtitles: list };
    });
  };

  const addSubtitleRow = () => {
    setDraft((d) => (d ? { ...d, subtitles: [...d.subtitles, emptySubtitle()] } : d));
  };

  const removeSubtitleRow = (index: number) => {
    setDraft((d) => (d ? { ...d, subtitles: d.subtitles.filter((_, i) => i !== index) } : d));
  };

  if (loading || !draft) {
    return (
      <section className="space-y-4">
        <AdminPageTitle title="جزئیات فیلم" />
        <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-6 text-slate-300">
          در حال دریافت اطلاعات فیلم...
        </div>
      </section>
    );
  }

  const taxonomyLabels: Record<TaxonomyKey, string> = {
    genres: "ژانرها",
    countries: "کشورها",
    languages: "زبان‌ها",
    networks: "شبکه‌ها",
    directors: "کارگردان‌ها",
    categories: "دسته‌بندی‌ها",
  };

  return (
    <section className="space-y-4">
      <form onSubmit={onSubmit} className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <AdminPageTitle title={draft.fa_title || draft.title} />
          <Link
            to="/admin/movies"
            className="inline-flex rounded-lg border border-slate-700 px-4 py-2 text-sm text-slate-200 transition hover:border-indigo-400 hover:text-indigo-300"
          >
            بازگشت به لیست
          </Link>
        </div>

        {draft.banner ? (
          <div className="overflow-hidden rounded-2xl border border-slate-800">
            <img src={assetUrl(draft.banner)} alt="" className="h-40 w-full object-cover sm:h-52" />
          </div>
        ) : null}

        <div className="flex flex-wrap gap-4">
          {draft.thumbnail ? (
            <div className="w-full max-w-[200px] overflow-hidden rounded-2xl border border-slate-800">
              <img src={assetUrl(draft.thumbnail)} alt="" className="aspect-[2/3] w-full object-cover" />
            </div>
          ) : (
            <div className="flex aspect-[2/3] w-full max-w-[200px] items-center justify-center rounded-2xl border border-slate-800 bg-slate-900/60 text-sm text-slate-500">
              بدون تصویر
            </div>
          )}
        </div>

        <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-5">
          <h3 className="mb-4 text-sm font-semibold text-slate-200">اطلاعات اصلی</h3>
          <div className="grid gap-4 md:grid-cols-2">
            <label className="space-y-2">
              <span className="text-xs text-slate-400">عنوان</span>
              <input
                className={inputClass}
                value={draft.title}
                onChange={(e) => setDraft((d) => (d ? { ...d, title: e.target.value } : d))}
              />
            </label>
            <label className="space-y-2">
              <span className="text-xs text-slate-400">عنوان فارسی</span>
              <input
                className={inputClass}
                value={draft.fa_title}
                onChange={(e) => setDraft((d) => (d ? { ...d, fa_title: e.target.value } : d))}
              />
            </label>
            <label className="space-y-2">
              <span className="text-xs text-slate-400">نوع</span>
              <select
                className={inputClass}
                value={draft.type}
                onChange={(e) => setDraft((d) => (d ? { ...d, type: e.target.value } : d))}
              >
                <option value="">انتخاب کنید</option>
                {moviTypeOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
                {draft.type && !moviTypeOptions.some((o) => o.value === draft.type) ? (
                  <option value={draft.type}>{draft.type}</option>
                ) : null}
              </select>
            </label>
            <label className="flex items-center gap-2 md:col-span-2">
              <input
                type="checkbox"
                checked={draft.is_series}
                onChange={(e) => setDraft((d) => (d ? { ...d, is_series: e.target.checked } : d))}
                className="h-4 w-4 accent-indigo-500"
              />
              <span className="text-sm text-slate-300">سریال است</span>
            </label>
            <label className="flex items-center gap-2 md:col-span-2">
              <input
                type="checkbox"
                checked={draft.use_auto_description}
                onChange={(e) => setDraft((d) => (d ? { ...d, use_auto_description: e.target.checked } : d))}
                className="h-4 w-4 accent-indigo-500"
              />
              <span className="text-sm text-slate-300">استفاده از توضیحات خودکار</span>
            </label>
            <label className="space-y-2 md:col-span-2">
              <span className="text-xs text-slate-400">توضیحات</span>
              <textarea
                rows={4}
                className={inputClass}
                value={draft.description}
                onChange={(e) => setDraft((d) => (d ? { ...d, description: e.target.value } : d))}
              />
            </label>
            <div className="space-y-2 md:col-span-2">
              <span className="text-xs text-slate-400">توضیحات تکمیلی</span>
              <HtmlEditor
                value={draft.full_description ?? ""}
                onChange={(html) => setDraft((d) => (d ? { ...d, full_description: html } : d))}
                placeholder="توضیحات تکمیلی را وارد کنید..."
                minHeight="280px"
              />
            </div>
            <label className="space-y-2 md:col-span-2">
              <span className="text-xs text-slate-400">کلمات کلیدی</span>
              <input
                className={inputClass}
                value={draft.keywords}
                onChange={(e) => setDraft((d) => (d ? { ...d, keywords: e.target.value } : d))}
              />
            </label>
          </div>
        </div>

        <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-5">
          <h3 className="mb-4 text-sm font-semibold text-slate-200">امتیازها و شناسه‌ها</h3>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {(
              [
                ["imdb_id", "IMDB ID"],
                ["imdb_rate", "IMDB امتیاز", "number"],
                ["imdb_votes", "IMDB رأی", "number"],
                ["rotten_tomatoes", "Rotten Tomatoes", "number"],
                ["metacritic", "Metacritic", "number"],
                ["mdl", "MDL", "number"],
                ["mdl_id", "MDL ID"],
                ["mal", "MAL", "number"],
                ["mal_id", "MAL ID"],
              ] as const
            ).map(([key, label, kind]) => (
              <label key={key} className="space-y-2">
                <span className="text-xs text-slate-400">{label}</span>
                <input
                  type={kind === "number" ? "number" : "text"}
                  className={inputClass}
                  value={String((draft as never)[key] ?? "")}
                  onChange={(e) => {
                    const raw = e.target.value;
                    setDraft((d) => {
                      if (!d) return d;
                      if (kind === "number") {
                        const n = raw === "" ? 0 : Number(raw);
                        return { ...d, [key]: Number.isNaN(n) ? 0 : n };
                      }
                      return { ...d, [key]: raw };
                    });
                  }}
                />
              </label>
            ))}
          </div>
        </div>

        <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-5">
          <h3 className="mb-4 text-sm font-semibold text-slate-200">تاریخ و وضعیت</h3>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            <label className="space-y-2">
              <span className="text-xs text-slate-400">تاریخ انتشار</span>
              <input
                className={inputClass}
                value={draft.release_date}
                onChange={(e) => setDraft((d) => (d ? { ...d, release_date: e.target.value } : d))}
              />
            </label>
            <label className="space-y-2">
              <span className="text-xs text-slate-400">سال شروع</span>
              <input
                type="number"
                className={inputClass}
                value={draft.start_year || ""}
                onChange={(e) =>
                  setDraft((d) => (d ? { ...d, start_year: e.target.value === "" ? 0 : Number(e.target.value) } : d))
                }
              />
            </label>
            <label className="space-y-2">
              <span className="text-xs text-slate-400">سال پایان</span>
              <input
                type="number"
                className={inputClass}
                value={draft.end_year || ""}
                onChange={(e) =>
                  setDraft((d) => (d ? { ...d, end_year: e.target.value === "" ? 0 : Number(e.target.value) } : d))
                }
              />
            </label>
            <label className="space-y-2">
              <span className="text-xs text-slate-400">رده سنی</span>
              <input
                className={inputClass}
                value={draft.age_rating}
                onChange={(e) => setDraft((d) => (d ? { ...d, age_rating: e.target.value } : d))}
              />
            </label>
            <label className="space-y-2">
              <span className="text-xs text-slate-400">وضعیت</span>
              <input
                className={inputClass}
                value={draft.status}
                onChange={(e) => setDraft((d) => (d ? { ...d, status: e.target.value } : d))}
              />
            </label>
            <label className="space-y-2 md:col-span-2 lg:col-span-3">
              <span className="text-xs text-slate-400">متن به‌روزرسانی</span>
              <input
                className={inputClass}
                value={draft.update_text}
                onChange={(e) => setDraft((d) => (d ? { ...d, update_text: e.target.value } : d))}
              />
            </label>
          </div>
        </div>

        <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-5">
          <h3 className="mb-4 text-sm font-semibold text-slate-200">تنظیمات و رسانه</h3>
          <div className="grid gap-4 md:grid-cols-2">
            <label className="space-y-2">
              <span className="text-xs text-slate-400">مدت (دقیقه)</span>
              <input
                type="number"
                className={inputClass}
                value={draft.duration || ""}
                onChange={(e) =>
                  setDraft((d) => (d ? { ...d, duration: e.target.value === "" ? 0 : Number(e.target.value) } : d))
                }
              />
            </label>
            <label className="space-y-2 md:col-span-2">
              <span className="text-xs text-slate-400">تامبنیل (مسیر یا URL)</span>
              <input
                className={inputClass}
                value={draft.thumbnail}
                onChange={(e) => setDraft((d) => (d ? { ...d, thumbnail: e.target.value } : d))}
              />
            </label>
            <label className="space-y-2 md:col-span-2">
              <span className="text-xs text-slate-400">بنر (مسیر یا URL)</span>
              <input
                className={inputClass}
                value={draft.banner}
                onChange={(e) => setDraft((d) => (d ? { ...d, banner: e.target.value } : d))}
              />
            </label>
            <label className="space-y-2 md:col-span-2">
              <span className="text-xs text-slate-400">تریلر (مسیر یا URL)</span>
              <input
                className={inputClass}
                value={draft.trailer}
                onChange={(e) => setDraft((d) => (d ? { ...d, trailer: e.target.value } : d))}
              />
            </label>
            <label className="space-y-2 md:col-span-2">
              <span className="text-xs text-slate-400">لینک منبع</span>
              <input
                className={inputClass}
                value={draft.source_link}
                onChange={(e) => setDraft((d) => (d ? { ...d, source_link: e.target.value } : d))}
              />
            </label>
            <label className="space-y-2">
              <span className="text-xs text-slate-400">نسخه</span>
              <input
                type="number"
                className={inputClass}
                value={draft.version ?? ""}
                onChange={(e) =>
                  setDraft((d) => (d ? { ...d, version: e.target.value === "" ? 0 : Number(e.target.value) } : d))
                }
              />
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={draft.is_dubbed}
                onChange={(e) => setDraft((d) => (d ? { ...d, is_dubbed: e.target.checked } : d))}
                className="h-4 w-4 accent-indigo-500"
              />
              <span className="text-sm text-slate-300">دوبله</span>
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={draft.is_subbed}
                onChange={(e) => setDraft((d) => (d ? { ...d, is_subbed: e.target.checked } : d))}
                className="h-4 w-4 accent-indigo-500"
              />
              <span className="text-sm text-slate-300">زیرنویس</span>
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={draft.is_free}
                onChange={(e) => setDraft((d) => (d ? { ...d, is_free: e.target.checked } : d))}
                className="h-4 w-4 accent-indigo-500"
              />
              <span className="text-sm text-slate-300">رایگان</span>
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={draft.is_locked}
                onChange={(e) => setDraft((d) => (d ? { ...d, is_locked: e.target.checked } : d))}
                className="h-4 w-4 accent-indigo-500"
              />
              <span className="text-sm text-slate-300">قفل</span>
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={draft.is_featured}
                onChange={(e) => setDraft((d) => (d ? { ...d, is_featured: e.target.checked } : d))}
                className="h-4 w-4 accent-indigo-500"
              />
              <span className="text-sm text-slate-300">ویژه</span>
            </label>
            <label className="flex items-center gap-2 md:col-span-2">
              <input
                type="checkbox"
                checked={draft.show_in_slider}
                onChange={(e) => setDraft((d) => (d ? { ...d, show_in_slider: e.target.checked } : d))}
                className="h-4 w-4 accent-indigo-500"
              />
              <span className="text-sm text-slate-300">نمایش در اسلایدر</span>
            </label>
          </div>
        </div>

        <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-5">
          <h3 className="mb-4 text-sm font-semibold text-slate-200">آمار</h3>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <div className="space-y-2">
              <span className="text-xs text-slate-400">بازدید</span>
              <div className="rounded-lg border border-slate-800 bg-slate-950/50 px-3 py-2 text-sm text-slate-100">
                {draft.movie_stats.views}
              </div>
            </div>
            <div className="space-y-2">
              <span className="text-xs text-slate-400">لایک</span>
              <div className="rounded-lg border border-slate-800 bg-slate-950/50 px-3 py-2 text-sm text-slate-100">
                {draft.movie_stats.likes}
              </div>
            </div>
            <div className="space-y-2">
              <span className="text-xs text-slate-400">دیسلایک</span>
              <div className="rounded-lg border border-slate-800 bg-slate-950/50 px-3 py-2 text-sm text-slate-100">
                {draft.movie_stats.dislikes}
              </div>
            </div>
          </div>
        </div>

        {(Object.keys(taxonomyLabels) as TaxonomyKey[]).map((key) => (
          <div key={key} className="rounded-2xl border border-slate-800 bg-slate-900/60 p-5">
            <h3 className="mb-3 text-sm font-semibold text-slate-200">{taxonomyLabels[key]}</h3>
            <p className="mb-3 text-xs text-slate-500">
              جستجو کنید و از نتایج اضافه کنید؛ نام و اسلاگ فقط‌خوانده‌اند.
            </p>
            <div className="mb-4 flex flex-wrap gap-2">
              <input
                className={`${inputClass} min-w-[12rem] flex-1`}
                placeholder="متن جستجو…"
                value={taxQuery[key] ?? ""}
                onChange={(e) => setTaxQuery((q) => ({ ...q, [key]: e.target.value }))}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    void searchTaxonomyKind(key);
                  }
                }}
              />
              <button
                type="button"
                disabled={taxSearching[key]}
                onClick={() => void searchTaxonomyKind(key)}
                className="rounded-lg border border-slate-600 px-4 py-2 text-xs text-slate-200 transition hover:border-indigo-400 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {taxSearching[key] ? "در حال جستجو…" : "جستجو"}
              </button>
            </div>
            {(taxResults[key] ?? []).length > 0 ? (
              <ul className="mb-4 max-h-48 space-y-1 overflow-y-auto rounded-xl border border-slate-800 bg-slate-950/40 p-2">
                {(taxResults[key] ?? []).map((item) => {
                  const already = (draft[key] ?? []).some((t) => t.id === item.id);
                  return (
                    <li
                      key={`${key}-res-${item.id}`}
                      className="flex flex-wrap items-center justify-between gap-2 rounded-lg px-2 py-1.5 text-sm text-slate-200 hover:bg-slate-800/80"
                    >
                      <span>
                        {item.name}
                        {item.slug ? <span className="mr-2 text-xs text-slate-500">({item.slug})</span> : null}
                      </span>
                      <button
                        type="button"
                        disabled={already || !item.id}
                        onClick={() => addTaxonomyFromSearch(key, item)}
                        className="shrink-0 rounded border border-indigo-700 px-2 py-0.5 text-xs text-indigo-200 hover:border-indigo-500 disabled:cursor-not-allowed disabled:opacity-40"
                      >
                        {already ? "اضافه شده" : "افزودن"}
                      </button>
                    </li>
                  );
                })}
              </ul>
            ) : null}
            <div className="flex flex-wrap gap-2">
              {(draft[key] ?? []).map((row, index) => (
                <div
                  key={`${key}-${row.id}-${index}`}
                  className="flex max-w-full items-center gap-2 rounded-full border border-slate-700 bg-slate-950/60 py-1 pl-1 pr-2 text-sm text-slate-100"
                >
                  <span className="truncate px-2">
                    {row.name}
                    {row.slug ? <span className="mr-1 text-xs text-slate-500">· {row.slug}</span> : null}
                  </span>
                  <button
                    type="button"
                    onClick={() => removeTaxonomyRow(key, index)}
                    className="shrink-0 rounded-full border border-rose-900/80 px-2 py-0.5 text-xs text-rose-200 hover:border-rose-600"
                    aria-label="حذف"
                  >
                    حذف
                  </button>
                </div>
              ))}
            </div>
            {(draft[key] ?? []).length === 0 ? (
              <p className="mt-2 text-xs text-slate-600">هنوز موردی انتخاب نشده است.</p>
            ) : null}
          </div>
        ))}

        <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-5">
          <h3 className="mb-3 text-sm font-semibold text-slate-200">بازیگران</h3>
          <p className="mb-3 text-xs text-slate-500">
            افزودن از جستجوی تاکسونومی؛ نام ثابت، نقش و «ستاره» قابل ویرایش.
          </p>
          <div className="mb-4 flex flex-wrap gap-2">
            <input
              className={`${inputClass} min-w-[12rem] flex-1`}
              placeholder="نام بازیگر برای جستجو…"
              value={actorQuery}
              onChange={(e) => setActorQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  void searchActors();
                }
              }}
            />
            <button
              type="button"
              disabled={actorSearching}
              onClick={() => void searchActors()}
              className="rounded-lg border border-slate-600 px-4 py-2 text-xs text-slate-200 transition hover:border-indigo-400 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {actorSearching ? "در حال جستجو…" : "جستجو"}
            </button>
          </div>
          {actorResults.length > 0 ? (
            <ul className="mb-4 max-h-48 space-y-1 overflow-y-auto rounded-xl border border-slate-800 bg-slate-950/40 p-2">
              {actorResults.map((item) => {
                const already = draft.actors.some((a) => a.id === item.id);
                return (
                  <li
                    key={`actor-res-${item.id}`}
                    className="flex flex-wrap items-center justify-between gap-2 rounded-lg px-2 py-1.5 text-sm text-slate-200 hover:bg-slate-800/80"
                  >
                    <span>
                      {item.name}
                      {item.slug ? <span className="mr-2 text-xs text-slate-500">({item.slug})</span> : null}
                    </span>
                    <button
                      type="button"
                      disabled={already || !item.id}
                      onClick={() => addActorFromSearch(item)}
                      className="shrink-0 rounded border border-indigo-700 px-2 py-0.5 text-xs text-indigo-200 hover:border-indigo-500 disabled:cursor-not-allowed disabled:opacity-40"
                    >
                      {already ? "اضافه شده" : "افزودن"}
                    </button>
                  </li>
                );
              })}
            </ul>
          ) : null}
          <div className="space-y-3">
            {draft.actors.map((row, index) => (
              <div
                key={`actor-${row.id}-${index}`}
                className="grid gap-3 rounded-xl border border-slate-800 bg-slate-950/40 p-3 sm:grid-cols-2 lg:grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)_auto_auto]"
              >
                <div className="space-y-1">
                  <span className="text-[11px] text-slate-500">نام</span>
                  <div className={`${inputClass} cursor-default border-slate-800 bg-slate-950/60 text-slate-300`}>{row.name}</div>
                </div>
                <label className="space-y-1">
                  <span className="text-[11px] text-slate-500">نقش در فیلم</span>
                  <input
                    className={inputClass}
                    value={row.role}
                    onChange={(e) => updateActorRow(index, "role", e.target.value)}
                    placeholder="مثلاً نقش اصلی، صداپیشه…"
                  />
                </label>
                <label className="flex items-end gap-2 pb-0.5">
                  <input
                    type="checkbox"
                    checked={row.is_star}
                    onChange={(e) => updateActorRow(index, "is_star", e.target.checked)}
                    className="h-4 w-4 accent-indigo-500"
                  />
                  <span className="text-xs text-slate-400">ستاره</span>
                </label>
                <div className="flex items-end sm:col-span-2 lg:col-span-1">
                  <button
                    type="button"
                    onClick={() => removeActorRow(index)}
                    className="w-full rounded-lg border border-rose-800 px-3 py-2 text-xs text-rose-200 hover:border-rose-600 sm:w-auto"
                  >
                    حذف
                  </button>
                </div>
              </div>
            ))}
          </div>
          {draft.actors.length === 0 ? (
            <p className="mt-2 text-xs text-slate-600">هنوز بازیگری اضافه نشده است.</p>
          ) : null}
        </div>

        <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-5">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <h3 className="text-sm font-semibold text-slate-200">گزینه‌های دانلود</h3>
            <button
              type="button"
              onClick={addDownloadRow}
              className="rounded-lg border border-slate-600 px-3 py-1 text-xs text-slate-200 hover:border-indigo-400"
            >
              افزودن ردیف
            </button>
          </div>
          <div className="space-y-4">
            {draft.download_options.map((row, index) => (
              <div key={`dl-${index}`} className="grid gap-2 rounded-xl border border-slate-800 p-3 lg:grid-cols-6">
                {(
                  [
                    ["id", "شناسه", "readonly"],
                    ["season", "فصل", "number"],
                    ["episode", "قسمت", "number"],
                    ["quality", "کیفیت"],
                    ["quality_code", "کد کیفیت"],
                    ["format", "فرمت"],
                    ["type", "نوع"],
                    ["encoder", "انکودر"],
                    ["size", "حجم", "number"],
                    ["url", "URL", "text", "md:col-span-2"],
                  ] as const
                ).map(([field, ph, kind]) => (
                  <label key={field} className={`space-y-1 ${field === "url" ? "lg:col-span-2" : ""}`}>
                    <span className="text-[11px] text-slate-500">{ph}</span>
                    {field === "type" ? (
                      <select
                        className={inputClass}
                        value={row.type}
                        onChange={(e) => updateDownloadRow(index, "type", e.target.value)}
                      >
                        <option value="">انتخاب کنید</option>
                        {downloadOptionTypeOptions.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                        {row.type && !downloadOptionTypeOptions.some((o) => o.value === row.type) ? (
                          <option value={row.type}>{row.type}</option>
                        ) : null}
                      </select>
                    ) : kind === "readonly" ? (
                      <div className={`${inputClass} cursor-default border-slate-800 bg-slate-950/60 text-slate-300`}>
                        {String((row as never)[field] ?? "—")}
                      </div>
                    ) : (
                      <input
                        type={kind === "number" ? "number" : "text"}
                        className={inputClass}
                        value={kind === "number" ? String((row as never)[field] ?? "") : (row as never)[field]}
                        onChange={(e) => {
                          const v = e.target.value;
                          if (kind === "number") {
                            updateDownloadRow(index, field, v === "" ? 0 : Number(v));
                          } else {
                            updateDownloadRow(index, field, v);
                          }
                        }}
                      />
                    )}
                  </label>
                ))}
                <label className="flex items-center gap-2 lg:col-span-2">
                  <input
                    type="checkbox"
                    checked={row.is_x265}
                    onChange={(e) => updateDownloadRow(index, "is_x265", e.target.checked)}
                    className="h-4 w-4 accent-indigo-500"
                  />
                  <span className="text-xs text-slate-400">x265</span>
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={row.is_10bit}
                    onChange={(e) => updateDownloadRow(index, "is_10bit", e.target.checked)}
                    className="h-4 w-4 accent-indigo-500"
                  />
                  <span className="text-xs text-slate-400">10bit</span>
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={row.can_play_online}
                    onChange={(e) => updateDownloadRow(index, "can_play_online", e.target.checked)}
                    className="h-4 w-4 accent-indigo-500"
                  />
                  <span className="text-xs text-slate-400">پخش آنلاین</span>
                </label>
                <button
                  type="button"
                  onClick={() => removeDownloadRow(index)}
                  className="rounded-lg border border-rose-800 px-3 py-2 text-xs text-rose-200 lg:col-span-6"
                >
                  حذف این ردیف
                </button>
              </div>
            ))}
          </div>
        </div>

        <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-5">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <h3 className="text-sm font-semibold text-slate-200">زیرنویس‌ها</h3>
            <button
              type="button"
              onClick={addSubtitleRow}
              className="rounded-lg border border-slate-600 px-3 py-1 text-xs text-slate-200 hover:border-indigo-400"
            >
              افزودن ردیف
            </button>
          </div>
          <div className="space-y-4">
            {draft.subtitles.map((row, index) => (
              <div key={`sub-${index}`} className="grid gap-2 rounded-xl border border-slate-800 p-3 md:grid-cols-4">
                <label className="space-y-1">
                  <span className="text-[11px] text-slate-500">شناسه</span>
                  <input
                    type="number"
                    className={inputClass}
                    value={row.id || ""}
                    onChange={(e) => updateSubtitleRow(index, "id", e.target.value === "" ? 0 : Number(e.target.value))}
                  />
                </label>
                <label className="space-y-1 md:col-span-2">
                  <span className="text-[11px] text-slate-500">نام</span>
                  <input
                    className={inputClass}
                    value={row.name}
                    onChange={(e) => updateSubtitleRow(index, "name", e.target.value)}
                  />
                </label>
                <label className="space-y-1">
                  <span className="text-[11px] text-slate-500">فصل</span>
                  <input
                    type="number"
                    className={inputClass}
                    value={row.season}
                    onChange={(e) =>
                      updateSubtitleRow(index, "season", e.target.value === "" ? 0 : Number(e.target.value))
                    }
                  />
                </label>
                <label className="space-y-1">
                  <span className="text-[11px] text-slate-500">قسمت</span>
                  <input
                    type="number"
                    className={inputClass}
                    value={row.episode}
                    onChange={(e) =>
                      updateSubtitleRow(index, "episode", e.target.value === "" ? 0 : Number(e.target.value))
                    }
                  />
                </label>
                <label className="space-y-1">
                  <span className="text-[11px] text-slate-500">فرمت</span>
                  <input
                    className={inputClass}
                    value={row.format}
                    onChange={(e) => updateSubtitleRow(index, "format", e.target.value)}
                  />
                </label>
                <label className="space-y-1">
                  <span className="text-[11px] text-slate-500">حجم</span>
                  <input
                    type="number"
                    className={inputClass}
                    value={row.size}
                    onChange={(e) =>
                      updateSubtitleRow(index, "size", e.target.value === "" ? 0 : Number(e.target.value))
                    }
                  />
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={row.dubbed}
                    onChange={(e) => updateSubtitleRow(index, "dubbed", e.target.checked)}
                    className="h-4 w-4 accent-indigo-500"
                  />
                  <span className="text-xs text-slate-400">دوبله</span>
                </label>
                <label className="space-y-1 md:col-span-4">
                  <span className="text-[11px] text-slate-500">URL</span>
                  <input
                    className={inputClass}
                    value={row.url}
                    onChange={(e) => updateSubtitleRow(index, "url", e.target.value)}
                  />
                </label>
                <button
                  type="button"
                  onClick={() => removeSubtitleRow(index)}
                  className="rounded-lg border border-rose-800 px-3 py-2 text-xs text-rose-200 md:col-span-4"
                >
                  حذف این ردیف
                </button>
              </div>
            ))}
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <button
            type="submit"
            disabled={isSubmitting || isDeleting}
            className="rounded-lg bg-indigo-500 px-5 py-2.5 text-sm font-medium text-white transition hover:bg-indigo-400 disabled:opacity-50"
          >
            {isSubmitting ? "در حال ذخیره..." : "ذخیره تغییرات"}
          </button>

          <button
            type="button"
            onClick={onDelete}
            disabled={isDeleting || isSubmitting}
            className="rounded-lg bg-rose-500 px-5 py-2.5 text-sm font-medium text-white transition hover:bg-rose-400 disabled:opacity-50"
          >
            {isDeleting ? "در حال حذف..." : "حذف فیلم"}
          </button>
        </div>
      </form>
    </section>
  );
}
